/**
 * Member Beneficiaries Management Page
 * 
 * Allows members to view, add, edit beneficiaries with approval workflow
 * - Maximum 5 beneficiaries per member
 * - Primary beneficiary: max 70 years (user must select)
 * - Additional beneficiaries: max 21 years
 * - Members cannot delete beneficiaries directly
 * - Members may submit a removal request; removal performed by admin only
 * - All member-added beneficiaries require admin approval
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import apiClient from '@/utils/api';
import { toast } from 'sonner';
import MemberLayout from '@/components/MemberLayout';
import { trackEvent, USAGE_EVENTS } from '@/utils/usageTracker';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2,
  Clock, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Info,
  User,
  Calendar,
  Heart,
  Crown
} from 'lucide-react';

const PRIMARY_MAX_AGE = 70;
const ADDITIONAL_MAX_AGE = 21;
const MAX_BENEFICIARIES = 5;

const MemberBeneficiaries = () => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    total_approved: 0,
    total_pending: 0,
    total_removal_requested: 0,
    max_beneficiaries: MAX_BENEFICIARIES,
    can_add_more: true
  });

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState({
    full_name: '',
    date_of_birth: '',
    relationship: '',
    is_primary: false
  });
  const [removalReason, setRemovalReason] = useState('');
  const [ageError, setAgeError] = useState('');

  useEffect(() => {
    trackEvent(USAGE_EVENTS.VIEW_BENEFICIARIES);
    loadBeneficiaries();
    loadNotifications();
  }, []);

  const loadBeneficiaries = async () => {
    try {
      const response = await apiClient.get('/members/beneficiaries');
      setBeneficiaries(response.data.beneficiaries || []);
      setStats({
        total_approved: response.data.total_approved || 0,
        total_pending: response.data.total_pending || 0,
        total_removal_requested: response.data.total_removal_requested || 0,
        max_beneficiaries: response.data.max_beneficiaries || MAX_BENEFICIARIES,
        can_add_more: response.data.can_add_more !== false
      });
    } catch (error) {
      toast.error('Failed to load beneficiaries');
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await apiClient.get('/notifications/unread');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 0;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return Math.max(0, age);
  };

  const validateAge = (dateOfBirth, isPrimary) => {
    const age = calculateAge(dateOfBirth);
    
    if (isPrimary) {
      if (age > PRIMARY_MAX_AGE) {
        return `Primary beneficiary cannot be older than ${PRIMARY_MAX_AGE} years. Current age: ${age}`;
      }
    } else {
      if (age > ADDITIONAL_MAX_AGE) {
        return `Non-primary beneficiaries cannot be older than ${ADDITIONAL_MAX_AGE} years. Current age: ${age}`;
      }
    }
    return '';
  };

  const handleDateChange = (value) => {
    setForm({ ...form, date_of_birth: value });
    const error = validateAge(value, form.is_primary);
    setAgeError(error);
  };

  const handlePrimaryChange = (checked) => {
    setForm({ ...form, is_primary: checked });
    if (form.date_of_birth) {
      const error = validateAge(form.date_of_birth, checked);
      setAgeError(error);
    }
  };

  const resetForm = () => {
    setForm({
      full_name: '',
      date_of_birth: '',
      relationship: '',
      is_primary: false
    });
    setAgeError('');
  };

  const handleAddBeneficiary = async () => {
    const error = validateAge(form.date_of_birth, form.is_primary);
    if (error) {
      setAgeError(error);
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('/members/beneficiaries', form);
      trackEvent(USAGE_EVENTS.ADD_BENEFICIARY);
      toast.success('Beneficiary added - awaiting admin approval');
      setAddDialogOpen(false);
      resetForm();
      loadBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add beneficiary');
    } finally {
      setSaving(false);
    }
  };

  const handleEditBeneficiary = async () => {
    const error = validateAge(form.date_of_birth, form.is_primary);
    if (error) {
      setAgeError(error);
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.put(`/members/beneficiaries/${selectedBeneficiary.beneficiary_id}`, form);
      trackEvent(USAGE_EVENTS.EDIT_BENEFICIARY);
      toast.success(response.data.requires_approval 
        ? 'Beneficiary updated - changes await admin approval' 
        : 'Beneficiary updated successfully');
      setEditDialogOpen(false);
      setSelectedBeneficiary(null);
      resetForm();
      loadBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update beneficiary');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestRemoval = async () => {
    if (!selectedBeneficiary) return;

    setSaving(true);
    try {
      await apiClient.post(`/members/beneficiaries/${selectedBeneficiary.beneficiary_id}/request-removal?reason=${encodeURIComponent(removalReason)}`);
      toast.success('Removal request submitted - awaiting admin approval');
      setRemovalDialogOpen(false);
      setSelectedBeneficiary(null);
      setRemovalReason('');
      loadBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit removal request');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setForm({
      full_name: beneficiary.full_name || '',
      date_of_birth: beneficiary.date_of_birth || '',
      relationship: beneficiary.relationship || '',
      is_primary: beneficiary.is_primary || false
    });
    setAgeError('');
    setEditDialogOpen(true);
  };

  const openRemovalDialog = (beneficiary) => {
    setSelectedBeneficiary(beneficiary);
    setRemovalReason('');
    setRemovalDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="w-3 h-3 mr-1" />Pending Approval</Badge>;
      case 'removal_requested':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><Trash2 className="w-3 h-3 mr-1" />Removal Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Check if there's already a primary beneficiary
  const hasPrimaryBeneficiary = beneficiaries.some(b => b.is_primary && b.status !== 'rejected');

  if (loading) {
    return (
      <MemberLayout currentPage="beneficiaries" title="Beneficiaries" notificationCount={notifications.length}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </MemberLayout>
    );
  }

  return (
    <MemberLayout currentPage="beneficiaries" title="Beneficiaries" notificationCount={notifications.length}>
      {/* Info Card */}
      <Card className="mb-6 bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Beneficiary Rules</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Maximum {stats.max_beneficiaries} beneficiaries allowed</li>
                <li>Primary beneficiary: maximum age {PRIMARY_MAX_AGE} years</li>
                <li>Other beneficiaries: maximum age {ADDITIONAL_MAX_AGE} years</li>
                <li>All new beneficiaries require administrator approval</li>
                <li>Only approved beneficiaries are included in billing</li>
                <li>To remove a beneficiary, submit a removal request for admin approval</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_approved}</p>
            <p className="text-sm text-muted-foreground">Approved</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_pending}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Trash2 className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{stats.total_removal_requested}</p>
            <p className="text-sm text-muted-foreground">Removal Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <Users className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold">{beneficiaries.length} / {stats.max_beneficiaries}</p>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent>
        </Card>
      </div>

      {/* Beneficiaries List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                My Beneficiaries
              </CardTitle>
              <CardDescription>Manage your registered beneficiaries</CardDescription>
            </div>
            {stats.can_add_more ? (
              <Button onClick={() => { resetForm(); setAddDialogOpen(true); }} data-testid="add-beneficiary-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Beneficiary
              </Button>
            ) : (
              <Button disabled variant="outline">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Limit Reached ({stats.max_beneficiaries})
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {beneficiaries.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Beneficiaries Yet</h3>
              <p className="text-muted-foreground mb-4">Add your first beneficiary to get started</p>
              <Button onClick={() => { resetForm(); setAddDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Beneficiary
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {beneficiaries.map((beneficiary, index) => (
                <div 
                  key={beneficiary.beneficiary_id || index} 
                  className={`border rounded-lg p-4 ${
                    beneficiary.status === 'removal_requested' ? 'bg-red-50 border-red-200' :
                    beneficiary.status === 'pending' ? 'bg-amber-50 border-amber-200' :
                    beneficiary.status === 'rejected' ? 'bg-gray-50 border-gray-200' :
                    'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium text-lg">{beneficiary.full_name}</h4>
                        {beneficiary.is_primary && (
                          <Badge variant="outline" className="text-xs bg-purple-50 border-purple-300 text-purple-700">
                            <Crown className="w-3 h-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                        {getStatusBadge(beneficiary.status)}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          <span>{beneficiary.relationship}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{beneficiary.date_of_birth || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Age: {beneficiary.age || calculateAge(beneficiary.date_of_birth)} years</span>
                        </div>
                        {beneficiary.added_at && (
                          <div className="text-xs">
                            Added: {new Date(beneficiary.added_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {beneficiary.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">{beneficiary.notes}</p>
                      )}
                      {beneficiary.status === 'removal_requested' && beneficiary.removal_reason && (
                        <p className="text-sm text-red-600 mt-2">
                          <strong>Removal reason:</strong> {beneficiary.removal_reason}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {beneficiary.status !== 'removal_requested' && beneficiary.status !== 'rejected' && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => openEditDialog(beneficiary)}
                            data-testid={`edit-beneficiary-${index}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {beneficiary.status === 'approved' && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => openRemovalDialog(beneficiary)}
                              data-testid={`remove-beneficiary-${index}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Beneficiary Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Beneficiary
            </DialogTitle>
            <DialogDescription>
              New beneficiaries require administrator approval before becoming active.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Enter full name"
                data-testid="beneficiary-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="date_of_birth">Date of Birth *</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => handleDateChange(e.target.value)}
                data-testid="beneficiary-dob-input"
              />
              {form.date_of_birth && (
                <p className="text-sm text-muted-foreground mt-1">
                  Age: {calculateAge(form.date_of_birth)} years
                </p>
              )}
              {ageError && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {ageError}
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="relationship">Relationship *</Label>
              <Input
                id="relationship"
                value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                placeholder="e.g., Spouse, Child, Parent"
                data-testid="beneficiary-relationship-input"
              />
            </div>

            <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <Checkbox
                id="is_primary"
                checked={form.is_primary}
                onCheckedChange={handlePrimaryChange}
                disabled={hasPrimaryBeneficiary}
                data-testid="beneficiary-primary-checkbox"
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="is_primary"
                  className={`text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1 ${hasPrimaryBeneficiary ? 'text-muted-foreground' : ''}`}
                >
                  <Crown className="w-4 h-4" />
                  Set as Primary Beneficiary
                </label>
                <p className="text-xs text-muted-foreground">
                  {hasPrimaryBeneficiary 
                    ? 'A primary beneficiary already exists' 
                    : `Primary beneficiary can be up to ${PRIMARY_MAX_AGE} years old`}
                </p>
              </div>
            </div>

            {!form.is_primary && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Non-primary beneficiaries must be {ADDITIONAL_MAX_AGE} years or younger.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddBeneficiary} 
              disabled={saving || !form.full_name || !form.date_of_birth || !form.relationship || ageError}
              data-testid="save-beneficiary-btn"
            >
              {saving ? 'Saving...' : 'Add Beneficiary'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Beneficiary Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Beneficiary
            </DialogTitle>
            <DialogDescription>
              Changes to approved beneficiaries will require re-approval.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_full_name">Full Name *</Label>
              <Input
                id="edit_full_name"
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                placeholder="Enter full name"
              />
            </div>
            
            <div>
              <Label htmlFor="edit_date_of_birth">Date of Birth *</Label>
              <Input
                id="edit_date_of_birth"
                type="date"
                value={form.date_of_birth}
                onChange={(e) => handleDateChange(e.target.value)}
              />
              {form.date_of_birth && (
                <p className="text-sm text-muted-foreground mt-1">
                  Age: {calculateAge(form.date_of_birth)} years
                </p>
              )}
              {ageError && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" />
                  {ageError}
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="edit_relationship">Relationship *</Label>
              <Input
                id="edit_relationship"
                value={form.relationship}
                onChange={(e) => setForm({ ...form, relationship: e.target.value })}
                placeholder="e.g., Spouse, Child, Parent"
              />
            </div>

            <div className="flex items-center space-x-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
              <Checkbox
                id="edit_is_primary"
                checked={form.is_primary}
                onCheckedChange={handlePrimaryChange}
                disabled={hasPrimaryBeneficiary && !selectedBeneficiary?.is_primary}
              />
              <div className="grid gap-1.5 leading-none">
                <label
                  htmlFor="edit_is_primary"
                  className="text-sm font-medium leading-none flex items-center gap-1"
                >
                  <Crown className="w-4 h-4" />
                  Primary Beneficiary
                </label>
                <p className="text-xs text-muted-foreground">
                  Primary beneficiary can be up to {PRIMARY_MAX_AGE} years old
                </p>
              </div>
            </div>

            {selectedBeneficiary?.status === 'approved' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Changes to approved beneficiaries will require re-approval.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleEditBeneficiary} 
              disabled={saving || !form.full_name || !form.date_of_birth || !form.relationship || ageError}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Removal Dialog */}
      <AlertDialog open={removalDialogOpen} onOpenChange={setRemovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Request Beneficiary Removal
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are requesting to remove <strong>{selectedBeneficiary?.full_name}</strong> from your beneficiaries.
              This request will be reviewed by an administrator. Only administrators can remove beneficiaries.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Label htmlFor="removal_reason">Reason for removal (optional)</Label>
            <Textarea
              id="removal_reason"
              value={removalReason}
              onChange={(e) => setRemovalReason(e.target.value)}
              placeholder="Please provide a reason for this removal request..."
              className="mt-2"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRequestRemoval}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? 'Submitting...' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MemberLayout>
  );
};

export default MemberBeneficiaries;
